import { Edit, RefreshCw, Target, Trash2, TrendingDown, TrendingUp, Upload } from "lucide-react";

const RequirementsSection = ({ ctx }) => {
  const {
    handleDeleteRequirement,
    handleEditRequirement,
    isMasterAdmin,
    openDialog,
    requirements,
    setUploadDialogOpen,
    syncRequirements
  } = ctx;
  return (
    <>
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="text-slate-600">Tracked: </span>
                <span className="font-bold text-primary">{requirements.length}</span>
                <span className="text-slate-600 mx-2">|</span>
                <span className="text-slate-600">Over-consumed: </span>
                <span className="font-bold text-error">{requirements.filter(r => r.variance < 0).length}</span>
                <span className="text-slate-600 mx-2">|</span>
                <span className="text-slate-600">On Track: </span>
                <span className="font-bold text-success">{requirements.filter(r => r.variance >= 0).length}</span>
              </div>
            </div>
            <div className="flex space-x-2">
              {isMasterAdmin && (
              <>
              <button
                onClick={() => setUploadDialogOpen(true)}
                className="btn-outline flex items-center space-x-2"
                data-testid="upload-excel-button"
              >
                <Upload className="w-4 h-4" />
                <span>Upload Excel</span>
              </button>
              <button
                onClick={syncRequirements}
                className="btn-outline flex items-center space-x-2"
                data-testid="sync-requirements-button"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Sync Consumption</span>
              </button>
              <button
                onClick={() => openDialog("requirement")}
                data-testid="add-requirement-button"
                className="btn-primary flex items-center space-x-2"
              >
                <Target className="w-4 h-4" />
                <span>Add Requirement</span>
              </button>
              </>
              )}
            </div>
          </div>
          
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Material</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Unit</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Planned</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Consumed</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Variance</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Remarks</th>
                  {isMasterAdmin && (
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {requirements.map((req) => {
                  const isOverConsumed = req.variance < 0;
                  const variancePct = req.variance_percentage?.toFixed(1) || 0;
                  return (
                    <tr key={req.id} className={`border-b border-slate-200 hover:bg-slate-50 ${isOverConsumed ? 'bg-red-50' : ''}`} data-testid={`requirement-row-${req.id}`}>
                      <td className="py-3 px-4 font-medium text-primary">{req.material_name}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm">{req.unit}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm font-semibold">{req.planned_quantity?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm">{req.consumed_quantity?.toFixed(2)}</td>
                      <td className={`py-3 px-4 text-right font-mono text-sm font-bold ${isOverConsumed ? 'text-error' : 'text-success'}`}>
                        <span className="flex items-center justify-end">
                          {isOverConsumed ? <TrendingDown className="w-4 h-4 mr-1" /> : <TrendingUp className="w-4 h-4 mr-1" />}
                          {req.variance?.toFixed(2)} ({variancePct}%)
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          isOverConsumed ? 'bg-error/20 text-error' : 
                          Math.abs(req.variance_percentage) < 5 ? 'bg-success/20 text-success' :
                          'bg-warning/20 text-warning'
                        }`}>
                          {isOverConsumed ? 'Over' : Math.abs(req.variance_percentage) < 5 ? 'On Track' : 'Under'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{req.remarks || '-'}</td>
                      {isMasterAdmin && (
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => handleEditRequirement(req)}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="Edit Requirement"
                            data-testid={`edit-requirement-${req.id}`}
                          >
                            <Edit className="w-4 h-4 text-primary" />
                          </button>
                          <button
                            onClick={() => handleDeleteRequirement(req.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-error" />
                          </button>
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {requirements.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Target className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Material Requirements</h3>
                <p className="text-slate-600 mb-4">Add planned quantities for materials to track consumption variance.</p>
              </div>
            )}
          </div>
        </div>
    </>
  );
};

export default RequirementsSection;
